import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown } from "lucide-react";
import {
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Button } from "../Button/Button";
import Input from "../Input/Input";
import { menuPanelClasses } from "../Menu/Menu";

export type DropdownItem = {
  value: string;
  /** Plain text: what the search matches and the trigger shows when picked. */
  label: string;
  /** Extra text the search matches besides `label`, e.g. "433" for "4-3-3". */
  searchText?: string;
  icon?: ReactNode;
  /** Drawn in the row instead of `label`, for a row that is a chip or a tag. */
  render?: ReactNode;
  /** A non-selectable group heading; hidden while searching. */
  header?: boolean;
};

type Variant = "primary" | "secondary";

const ROW_HEIGHT = 32;

// Focus the search on open only where it won't pop a phone keyboard.
const canHover = () => window.matchMedia?.("(hover: hover)").matches ?? false;

// Menu's edge and shadow, so the list stands off any panel behind it
const PANEL_CLASSES = cn(
  menuPanelClasses,
  "absolute z-50 mt-1 flex flex-col gap-1 rounded-[20px] p-2",
);

const DEFAULT_PANEL_WIDTH = 267;
const DEFAULT_TRIGGER_WIDTH = 200;

type RowProps = {
  item: DropdownItem;
  isSelected: boolean;
  multi: boolean;
  onClick: () => void;
};

const Row = ({ item, isSelected, multi, onClick }: RowProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex h-full w-full items-center gap-1.5 rounded-[20px] px-3 text-left text-sm transition-colors",
      isSelected
        ? "bg-blue-900/50 text-neutral-50"
        : "text-neutral-100 hover:bg-neutral-700",
    )}
  >
    {multi && (
      <span
        className={cn(
          "size-3 shrink-0 rounded-sm border",
          isSelected
            ? "border-blue-500 bg-blue-600"
            : "border-neutral-500 bg-transparent",
        )}
      />
    )}
    {item.icon && <span className="shrink-0">{item.icon}</span>}
    {item.render ?? <span className="truncate">{item.label}</span>}
  </button>
);

type ListProps = {
  items: DropdownItem[];
  selectedSet: Set<string>;
  multi: boolean;
  onItemClick: (item: DropdownItem) => void;
};

const VirtualList = ({ items, selectedSet, multi, onItemClick }: ListProps) => {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 6,
  });

  const totalHeight = virtualizer.getTotalSize();

  return (
    <div ref={parentRef} className="w-full max-h-64 overflow-y-auto">
      <div className="relative w-full" style={{ height: totalHeight }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              className="absolute top-0 left-0 w-full h-8"
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {item.header ? (
                <div className="flex h-full items-end px-3 pb-1 text-xs font-bold uppercase tracking-wide text-foreground-muted">
                  {item.label}
                </div>
              ) : (
                <Row
                  item={item}
                  isSelected={selectedSet.has(item.value)}
                  multi={multi}
                  onClick={() => onItemClick(item)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

type TriggerProps = {
  label: string;
  icon?: ReactNode;
  badgeCount?: number;
  variant: Variant;
  open: boolean;
  onClick: () => void;
  width: number;
  /** `width` becomes a minimum and the trigger grows to fit its label. */
  fitContent?: boolean;
  disabled?: boolean;
};

const Trigger = ({
  label,
  icon,
  badgeCount,
  variant,
  open,
  onClick,
  width,
  fitContent = false,
  disabled,
}: TriggerProps) => (
  // Width lives on the wrapper: the config's `important: true` makes Button's
  // own width utilities beat any inline width set directly on the button.
  <div
    style={
      fitContent
        ? { minWidth: width, width: "max-content", maxWidth: "100%" }
        : { width }
    }
  >
    <Button
      variant={variant}
      size="small"
      onClick={onClick}
      stretch
      disabled={disabled}
      className="px-3"
    >
      <span className="flex w-full items-center gap-1.5">
        {icon && <span className="shrink-0">{icon}</span>}
        <span className="flex-1 truncate text-left">{label}</span>
        {badgeCount !== undefined && badgeCount > 0 && (
          <span className="shrink-0 rounded-full bg-blue-700 px-1.5 text-xs text-neutral-50 shadow-button">
            {badgeCount}
          </span>
        )}
        <ChevronDown
          className={cn(
            "shrink-0 size-4 transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </span>
    </Button>
  </div>
);

/*
  Close-on-outside handler. Returns a stable ref to attach to the dropdown root;
  clicks anywhere outside that root close the dropdown.
*/
const useClickOutside = (
  onOutside: () => void,
  /** A portalled panel: outside the root in the DOM, inside it logically. */
  panelRef?: RefObject<HTMLDivElement | null>,
) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !panelRef?.current?.contains(target)
      ) {
        onOutside();
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [onOutside]);
  return ref;
};

type SharedProps = {
  items: DropdownItem[];
  variant?: Variant;
  searchPlaceholder?: string;
  /** Hides the search input in the panel — for short static lists. */
  hideSearch?: boolean;
  /**
   * Width in px for both the trigger button and the panel. When omitted the
   * defaults apply (trigger 200, panel 267).
   */
  width?: number;
  /** Trigger width in px on its own, when it should be narrower than the panel. */
  triggerWidth?: number;
  /** Which edge of the trigger the panel lines up with. */
  align?: "start" | "end";
};

type SingleProps = SharedProps & {
  placeholder: string;
  selected?: string;
  onChange: (item: DropdownItem) => void;
  /**
   * Replaces the built-in trigger button. As a render prop it is handed the
   * open state, for a trigger that shows it. Same shape as Popover's.
   */
  trigger?: ReactNode | ((open: boolean) => ReactNode);
  /** `width` becomes a minimum: the trigger grows to fit its label and the panel its longest one. */
  fitContent?: boolean;
  /** The trigger's text in place of the selected item's label, e.g. "Category: All". */
  triggerLabel?: string;
  /**
   * Renders the panel in a portal with fixed positioning, so a scrolling or
   * clipping ancestor (a Popover panel, a sheet) can't cut it off.
   */
  portal?: boolean;
  // Greys the built-in trigger and keeps the panel shut
  disabled?: boolean;
};

// Gap to the trigger and to the viewport edge for a portalled panel.
const PORTAL_GAP = 4;
const VIEWPORT_MARGIN = 12;

export const Dropdown = ({
  items,
  variant = "primary",
  searchPlaceholder,
  hideSearch = false,
  width,
  triggerWidth,
  placeholder,
  selected,
  onChange,
  trigger: customTrigger,
  align = "start",
  fitContent = false,
  triggerLabel: triggerLabelOverride,
  portal = false,
  disabled = false,
}: SingleProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [frozenOrder, setFrozenOrder] = useState<DropdownItem[] | null>(null);
  const [portalPos, setPortalPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const rootRef = useClickOutside(() => setOpen(false), panelRef);

  // Place the portalled panel under the trigger, above it when there's no room below.
  useLayoutEffect(() => {
    if (!open || !portal) {
      setPortalPos(null);
      return;
    }
    const compute = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const height = panelRef.current?.offsetHeight ?? 0;
      const panelWidth = panelRef.current?.offsetWidth ?? 0;
      const below = rect.bottom + PORTAL_GAP;
      const above = rect.top - PORTAL_GAP - height;
      const top =
        below + height > window.innerHeight - VIEWPORT_MARGIN &&
        above >= VIEWPORT_MARGIN
          ? above
          : below;
      const left = align === "end" ? rect.right - panelWidth : rect.left;
      // Same as Popover: only `left`, kept inside the viewport.
      setPortalPos({
        top,
        left: Math.min(
          Math.max(left, VIEWPORT_MARGIN),
          window.innerWidth - VIEWPORT_MARGIN - panelWidth,
        ),
      });
    };
    compute();
    window.addEventListener("resize", compute);
    // Capture phase so scrolling any ancestor container repositions the panel.
    window.addEventListener("scroll", compute, true);
    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("scroll", compute, true);
    };
  }, [open, portal, align, rootRef]);

  const selectedSet = useMemo(
    () => new Set(selected ? [selected] : []),
    [selected],
  );

  /*
    Snapshot the list on open and hold it while the panel is up, so a change
    to `items` mid-interaction cannot shift rows under the pointer. The order
    is the caller's, always: a picked row never jumps to the top.
  */
  useEffect(() => {
    if (open) {
      setFrozenOrder(items);
      setQuery("");
    } else {
      setFrozenOrder(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleItems = useMemo(() => {
    const source = frozenOrder ?? items;
    if (!query) return source;
    const q = query.toLowerCase();
    return source.filter(
      (it) =>
        !it.header &&
        `${it.label} ${it.searchText ?? ""}`.toLowerCase().includes(q),
    );
  }, [frozenOrder, items, query]);

  const selectedItem = items.find((it) => it.value === selected);
  const triggerLabel =
    triggerLabelOverride ?? selectedItem?.label ?? placeholder;

  const panel = (
    <div
      ref={panelRef}
      // The Popover around it must not take a click in here for an outside one.
      data-outside-click-ignore={portal || undefined}
      className={cn(
        PANEL_CLASSES,
        align === "end" && !portal && "right-0",
        portal && "fixed z-[130] mt-0",
        // Not `invisible`: a hidden input can't take the autofocus.
        portal && !portalPos && "opacity-0",
      )}
      style={{
        ...(fitContent
          ? { minWidth: width ?? DEFAULT_PANEL_WIDTH, width: "max-content" }
          : { width: width ?? DEFAULT_PANEL_WIDTH }),
        // Pinned in view until measured, so the focused input never scrolls the page.
        ...(portal && (portalPos ?? { top: 0, left: 0 })),
      }}
    >
      {!hideSearch && (
        <Input
          value={query}
          onChange={setQuery}
          placeholder={searchPlaceholder ?? t("search")}
          autoFocus={canHover()}
          size="small"
          className="bg-neutral-750"
        />
      )}
      <VirtualList
        items={visibleItems}
        selectedSet={selectedSet}
        multi={false}
        onItemClick={(item) => {
          onChange(item);
          setOpen(false);
        }}
      />
      {/* The virtual rows are absolute and can't size the panel; this hidden copy does. */}
      {fitContent && (
        <div aria-hidden className="invisible -mt-1 h-0 overflow-hidden">
          {items.map((item) => (
            <Row
              key={item.value}
              item={item}
              isSelected={false}
              multi={false}
              onClick={() => {}}
            />
          ))}
        </div>
      )}
    </div>
  );

  const triggerNode =
    typeof customTrigger === "function" ? customTrigger(open) : customTrigger;

  return (
    // Capped by its container, so a fitContent label truncates instead of overflowing.
    <div ref={rootRef} className="relative inline-block max-w-full">
      {triggerNode ? (
        // `contents` keeps the custom element's own layout; the click is caught
        // as it bubbles out of it.
        <span className="contents" onClick={() => setOpen((v) => !v)}>
          {triggerNode}
        </span>
      ) : (
        <Trigger
          label={triggerLabel}
          icon={selectedItem?.icon}
          variant={variant}
          open={open}
          onClick={() => !disabled && setOpen((v) => !v)}
          width={triggerWidth ?? width ?? DEFAULT_TRIGGER_WIDTH}
          fitContent={fitContent}
          disabled={disabled}
        />
      )}
      {open && (portal ? createPortal(panel, document.body) : panel)}
    </div>
  );
};

type MultiProps = SharedProps & {
  label: string;
  selected: string[];
  onChange: (item: DropdownItem) => void;
  /** Replaces the built-in trigger, as on the single Dropdown. */
  trigger?: ReactNode | ((open: boolean) => ReactNode);
  /** Classes for the root, e.g. `block w-full` for a full-row trigger. */
  className?: string;
};

export const MultiDropdown = ({
  items,
  variant = "primary",
  searchPlaceholder,
  hideSearch = false,
  width,
  triggerWidth,
  label,
  selected,
  onChange,
  align = "start",
  trigger: customTrigger,
  className,
}: MultiProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [frozenOrder, setFrozenOrder] = useState<DropdownItem[] | null>(null);
  const rootRef = useClickOutside(() => setOpen(false));

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  useEffect(() => {
    if (open) {
      setFrozenOrder(items);
      setQuery("");
    } else {
      setFrozenOrder(null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleItems = useMemo(() => {
    const source = frozenOrder ?? items;
    if (!query) return source;
    const q = query.toLowerCase();
    return source.filter(
      (it) =>
        !it.header &&
        `${it.label} ${it.searchText ?? ""}`.toLowerCase().includes(q),
    );
  }, [frozenOrder, items, query]);

  const triggerNode =
    typeof customTrigger === "function" ? customTrigger(open) : customTrigger;

  return (
    <div ref={rootRef} className={cn("relative inline-block", className)}>
      {triggerNode ? (
        <span className="contents" onClick={() => setOpen((v) => !v)}>
          {triggerNode}
        </span>
      ) : (
        <Trigger
          label={label}
          badgeCount={selected.length}
          variant={variant}
          open={open}
          onClick={() => setOpen((v) => !v)}
          width={triggerWidth ?? width ?? DEFAULT_TRIGGER_WIDTH}
        />
      )}
      {open && (
        <div
          className={cn(PANEL_CLASSES, align === "end" && "right-0")}
          style={{ width: width ?? DEFAULT_PANEL_WIDTH }}
        >
          {!hideSearch && (
            <Input
              value={query}
              onChange={setQuery}
              placeholder={searchPlaceholder ?? t("search")}
              autoFocus={canHover()}
              size="small"
              className="bg-neutral-750"
            />
          )}
          <VirtualList
            items={visibleItems}
            selectedSet={selectedSet}
            multi
            onItemClick={onChange}
          />
        </div>
      )}
    </div>
  );
};
